import { addBusinessDays } from 'date-fns'

import { ContractType } from '@/pages/instruments/new-instrument/constants/contract-types'
import { ContractSide } from '@/pages/instruments/new-instrument/constants/sides'
import {
  BarrierLegParams,
  PrimaryTerminalLegParams,
  ScheduleGenerator,
  ScheduleLeg,
  Strike,
} from '@/pages/instruments/new-instrument/schedule-builders/types'

export type VanillaSpreadOptions = {
  strikes: Strike[]
  contract_type: ContractType
  contract_side: ContractSide
  initial_observation_date: Date
  participation_rate: number
  maturity_date: Date
  settlement_cycle: number
  currency_id: number
  knockdown_input_level: number | null
}

const lowerBoundThreshold = 90
const upperBoundThreshold = 120

const buildPrimaryLowerLeg = (
  params: PrimaryTerminalLegParams<VanillaSpreadOptions>,
): ScheduleLeg => {
  const {
    contract_type,
    currency_id,
    strike,
    initial_observation_date,
    participation_rate,
    contract_side,
    maturity_date,
    settlement_cycle,
  } = params

  const rate_multiplier = contract_side === 'shortLowerLongUpper' ? -1 : 1

  return {
    leg_id: 1,
    option_type: 'Vanilla',
    contract_type,
    region_type: 'European',
    barrier_type: 'Up-and-In',
    currency_id,
    strike: strike.value,
    strike_percent: lowerBoundThreshold,
    coupon_rate: null,
    participation_rate: participation_rate * rate_multiplier,
    instrument_id: strike.underlying_id,
    initial_observation_date,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    start_observation_date: maturity_date,
    end_observation_date: maturity_date,
  }
}

const buildPrimaryUpperLeg = (
  params: PrimaryTerminalLegParams<VanillaSpreadOptions>,
): ScheduleLeg => {
  const {
    contract_type,
    currency_id,
    strike,
    contract_side,
    initial_observation_date,
    participation_rate,
    maturity_date,
    settlement_cycle,
  } = params

  const rate_multiplier = contract_side === 'shortLowerLongUpper' ? 1 : -1

  return {
    leg_id: 2,
    option_type: 'Vanilla',
    contract_type,
    region_type: 'European',
    barrier_type: 'Up-and-In',
    currency_id,
    strike: strike.value,
    strike_percent: upperBoundThreshold,
    participation_rate: participation_rate * rate_multiplier,
    coupon_rate: null,
    instrument_id: strike.underlying_id,
    initial_observation_date,
    pay_date: addBusinessDays(maturity_date, settlement_cycle),
    start_observation_date: maturity_date,
    end_observation_date: maturity_date,
  }
}

const buildBarrierLeg = (params: BarrierLegParams<VanillaSpreadOptions>): ScheduleLeg => {
  const { currency_id, strike, knockdown_input_level, final_leg, initial_observation_date } = params

  return {
    leg_id: 3,
    option_type: 'Barrier Vanilla',
    barrier_type: 'Down-and-In',
    contract_type: 'Put',
    region_type: 'European',
    currency_id,
    strike: strike.value,
    strike_percent: strike.percent,
    pay_date: final_leg.pay_date,
    start_observation_date: final_leg.start_observation_date,
    end_observation_date: final_leg.end_observation_date,
    instrument_id: strike.underlying_id,
    autocall_barrier_level: knockdown_input_level,
    payoff_barrier_level: knockdown_input_level,
    initial_observation_date,
    participation_rate: -1,
  }
}

export const VanillaSpreadGenerator: ScheduleGenerator<VanillaSpreadOptions> = {
  generate({ strikes, ...params }) {
    const legs: ScheduleLeg[] = []

    for (const strike of strikes) {
      const lowerLeg = buildPrimaryLowerLeg({ strike, ...params })
      const upperLeg = buildPrimaryUpperLeg({ strike, ...params })

      legs.push(lowerLeg, upperLeg)

      if (params.knockdown_input_level !== null) {
        legs.push(buildBarrierLeg({ strike, final_leg: upperLeg, ...params }))
      }
    }

    return legs
  },
}
